import React, { Suspense } from 'react';
import { Navigate, Route } from 'react-router-dom';

import RequireAdmin from './RequireAdmin';
import { adminSection } from '../../config/routeHandles';
import { Skeleton } from '../loading';

// The admin portal is ONE lazy chunk (webpackChunkName "admin", KPR-309): the
// layout and every section. Nothing here is imported eagerly, so the app's
// main bundle carries none of it, and the chunk is requested only when the
// lazy layout below renders — which is only past the guard. A person who is
// not an admin (or a reviewer, who sees Review alone) gets the 403 and never
// downloads a byte of admin code, nor makes an admin request. The server's
// own admin checks are unchanged; this keeps the code private too.
// Keep each one on one line: src/__tests__/appRoutes.test.js reads them.
const AdminLayout = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminLayout'));
const AdminDashboard = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminDashboard'));
const AdminUsers = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminUsers'));
const AdminBlocklist = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminBlocklist'));
const AdminEmailAllowances = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminEmailAllowances'));
const AdminReports = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminReports'));
const AdminFormAddresses = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminFormAddresses'));
const AdminEmails = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminEmails'));
const AdminAttachments = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminAttachments'));
const AdminAutomations = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminAutomations'));
const AdminNotifications = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminNotifications'));
const AdminTags = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminTags'));
const AdminHistory = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminHistory'));
const AdminRecentlyDeleted = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminRecentlyDeleted'));
const AdminTasks = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminTasks'));
const AdminPackages = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminPackages'));
const AdminDiscover = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminDiscover'));
const AdminPublishers = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminPublishers'));
const AdminReview = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminReview'));
const AdminStyleGuide = React.lazy(() => import(/* webpackChunkName: "admin" */ './AdminStyleGuide'));
// How keepr is built: the atlases, the engineering change log and the line
// counts. They moved here from /docs, which is public. Each keeps a chunk of
// its own (the change log's charts are heavy), still only past the guard.
const AdminAtlases = React.lazy(() => import('../docs/DocsAtlases'));
const AdminChangelog = React.lazy(() => import('../docs/DocsChangelog'));
const AdminCodebase = React.lazy(() => import('../docs/DocsCodebase'));

/** While the admin chunk arrives: the portal's footprint, never a spinner page. */
export function AdminChunkFallback() {
	return (
		<div className='admin-portal admin-portal--pending' aria-busy='true' data-testid='admin-chunk-pending'>
			<div className='admin-portal__work'>
				<Skeleton variant='line' count={6} />
			</div>
		</div>
	);
}

/**
 * The /admin portal route fragment, shared by src/index.js and the routing
 * tests so the redirects and nesting under test are the real configuration.
 * Returned as a fragment of <Route> elements (react-router flattens
 * fragments), NOT a component — `createRoutesFromElements` (and <Routes>)
 * only read <Route> children.
 *
 * src/index.js mounts it OUTSIDE the app shell (KPR-309): the portal is its
 * own UI, opened in its own browser tab from the rail, with its own header
 * (AdminLayout) and no rail. It sits inside the session guard there, so a
 * signed-out visitor signs in and comes back to the page they asked for.
 */
export default function adminRoutes() {
	return (
		<React.Fragment>
			<Route
				path='admin'
				element={(
					<RequireAdmin allowReviewer>
						<Suspense fallback={<AdminChunkFallback />}>
							<AdminLayout />
						</Suspense>
					</RequireAdmin>
				)}
			>
				<Route index element={<AdminDashboard />} handle={adminSection('dashboard')} />
				<Route path='users' element={<AdminUsers />} handle={adminSection('users')} />
				<Route path='blocklist' element={<AdminBlocklist />} handle={adminSection('blocklist')} />
				<Route path='allowances' element={<AdminEmailAllowances />} handle={adminSection('allowances')} />
				<Route path='reported-pages' element={<AdminReports />} handle={adminSection('reported-pages')} />
				<Route path='form-addresses' element={<AdminFormAddresses />} handle={adminSection('form-addresses')} />
				<Route path='emails' element={<AdminEmails />} handle={adminSection('emails')} />
				<Route path='attachments' element={<AdminAttachments />} handle={adminSection('attachments')} />
				<Route path='automations' element={<AdminAutomations />} handle={adminSection('automations')} />
				<Route path='notifications' element={<AdminNotifications />} handle={adminSection('notifications')} />
				<Route path='tags' element={<AdminTags />} handle={adminSection('tags')} />
				<Route path='history' element={<AdminHistory />} handle={adminSection('history')} />
				<Route path='deleted' element={<AdminRecentlyDeleted />} handle={adminSection('deleted')} />
				<Route path='tasks' element={<AdminTasks />} handle={adminSection('tasks')} />
				<Route path='packages' element={<AdminPackages />} handle={adminSection('packages')} />
				<Route path='discover' element={<AdminDiscover />} handle={adminSection('discover')} />
				<Route path='publishers' element={<AdminPublishers />} handle={adminSection('publishers')} />
				<Route path='review' element={<AdminReview />} handle={adminSection('review')} />
				<Route path='style-guide' element={<AdminStyleGuide />} handle={adminSection('style-guide')} />
				<Route path='atlases' element={<AdminAtlases />} handle={adminSection('atlases')} />
				<Route path='atlases/:atlasKey' element={<AdminAtlases />} handle={adminSection('atlases')} />
				<Route path='changelog' element={<AdminChangelog />} handle={adminSection('changelog')} />
				<Route path='codebase' element={<AdminCodebase />} handle={adminSection('codebase')} />
				{/* The Icons gallery became a section of the style guide (KPR-311). */}
				<Route path='icons' element={<Navigate to='/admin/style-guide#icons' replace />} />
				{/* The Colors page grew into the style guide; its path follows. */}
				<Route path='colors' element={<Navigate to='/admin/style-guide' replace />} />
			</Route>

			{/* The retired flat paths live on in bookmarks and history. */}
			<Route path='admin-users' element={<Navigate to='/admin/users' replace />} />
			<Route path='admin-emails' element={<Navigate to='/admin/emails' replace />} />
			<Route path='admin-attachments' element={<Navigate to='/admin/attachments' replace />} />
			<Route path='admin-icons' element={<Navigate to='/admin/style-guide#icons' replace />} />
			<Route path='admin-colors' element={<Navigate to='/admin/style-guide' replace />} />
		</React.Fragment>
	);
}
